import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, BarChart2, ClipboardList, Trophy, Timer, CheckCircle2, XCircle, Target } from 'lucide-react';
import { testResultService } from '../services/testResultService';
import { analyticsService, StudentBehaviorAnalytics } from '../services/analyticsService';
import { TestResult, StudentPerformanceAnalytics } from '../types/testResult';
import { SubjectPerformanceChart } from '../components/SubjectPerformanceChart';
import { MonthlyProgressChart } from '../components/MonthlyProgressChart';
import { DifficultyAnalysisChart } from '../components/DifficultyAnalysisChart';
import { TestResultCard } from '../components/TestResultCard';

type MainTab = 'dashboard' | 'per-exam' | 'weak-areas';

const getScoreTextColor = (pct: number) => {
  if (pct >= 75) return 'text-green-600';
  if (pct >= 50) return 'text-yellow-600';
  return 'text-red-600';
};

const getScoreBadgeClass = (pct: number) => {
  if (pct >= 75) return 'bg-green-100 text-green-800';
  if (pct >= 50) return 'bg-yellow-100 text-yellow-800';
  return 'bg-red-100 text-red-800';
};

const getBarColor = (pct: number) => {
  if (pct >= 75) return 'bg-green-500';
  if (pct >= 50) return 'bg-yellow-500';
  return 'bg-red-500';
};

const getStatusLabel = (pct: number) => {
  if (pct >= 75) return 'Strong';
  if (pct >= 50) return 'Developing';
  return 'Needs Work';
};

const getGrade = (pct: number) => {
  if (pct >= 90) return 'A+';
  if (pct >= 80) return 'A';
  if (pct >= 70) return 'B+';
  if (pct >= 60) return 'B';
  if (pct >= 50) return 'C';
  if (pct >= 40) return 'D';
  return 'F';
};

/**
 * Analytics Page - Unified hub for performance tracking, per-exam deep dives,
 * and weak area identification.
 */
export const AnalyticsPage: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<MainTab>('dashboard');

  // Shared data
  const [results, setResults] = useState<TestResult[]>([]);
  const [analytics, setAnalytics] = useState<StudentPerformanceAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Per-exam state
  const [selectedResult, setSelectedResult] = useState<TestResult | null>(null);
  const [behaviorData, setBehaviorData] = useState<StudentBehaviorAnalytics | null>(null);
  const [behaviorLoading, setBehaviorLoading] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [resultsData, analyticsData] = await Promise.all([
        testResultService.getStudentResults(),
        testResultService.getPerformanceAnalytics(),
      ]);
      const sorted = [...resultsData].sort((a, b) =>
        new Date(b.completedAt || b.startedAt).getTime() -
        new Date(a.completedAt || a.startedAt).getTime()
      );
      setResults(sorted);
      setAnalytics(analyticsData);
      if (sorted.length > 0) {
        setSelectedResult(sorted[0]);
        void loadBehaviorData(sorted[0].testId);
      }
    } catch {
      setError('Failed to load analytics. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadBehaviorData = async (testId: string) => {
    setBehaviorLoading(true);
    setBehaviorData(null);
    try {
      const data = await analyticsService.getTestAnalytics(testId);
      setBehaviorData(data);
    } catch {
      // Behavior data may not exist for all tests — silently skip
    } finally {
      setBehaviorLoading(false);
    }
  };

  const handleSelectResult = (result: TestResult) => {
    setSelectedResult(result);
    void loadBehaviorData(result.testId);
  };

  // Derived values
  const subjectPerformanceList = analytics?.subjectWisePerformance
    ? Object.values(analytics.subjectWisePerformance).sort((a, b) => b.averagePercentage - a.averagePercentage)
    : [];
  const weakSubjects = [...subjectPerformanceList].sort((a, b) => a.averagePercentage - b.averagePercentage);
  const difficultyList = analytics?.difficultyWisePerformance
    ? Object.values(analytics.difficultyWisePerformance)
    : [];
  const totalStudyHours = analytics?.totalTimeSpent
    ? Math.round(analytics.totalTimeSpent / 60)
    : 0;
  const bestPercentage = results.length > 0
    ? Math.max(...results.map(r => r.percentage))
    : 0;
  const recentCompletedResults = results
    .filter(r => r.status === 'COMPLETED' || r.status === 'SUBMITTED')
    .slice(0, 5);

  // ---- Loading / error states ----
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0456FF] mx-auto"></div>
          <p className="mt-4 text-[#0456FF]">Loading your analytics…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-4" aria-hidden="true" />
          <p className="text-gray-600 mb-4">{error}</p>
          <button onClick={loadData} className="px-4 py-2 bg-[#0456FF] text-white rounded-md hover:bg-[#0456FF]/90">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (results.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <BarChart2 className="w-16 h-16 text-gray-400 mx-auto mb-4" aria-hidden="true" />
          <h2 className="text-2xl font-bold text-gray-700 mb-2">No Data Yet</h2>
          <p className="text-gray-600 mb-4">Complete some tests to see your analytics</p>
          <button onClick={() => navigate('/tests')} className="px-4 py-2 bg-[#0456FF] text-white rounded-md hover:bg-[#0456FF]/90">
            Browse Tests
          </button>
        </div>
      </div>
    );
  }

  const tabs: { key: MainTab; label: string; Icon: React.FC<{ className?: string }> }[] = [
    { key: 'dashboard', label: 'Dashboard', Icon: BarChart2 },
    { key: 'per-exam', label: 'Per-Exam', Icon: ClipboardList },
    { key: 'weak-areas', label: 'Weak Areas', Icon: Target },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Page Header */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <h1 className="text-3xl font-bold text-[#0456FF]">Analytics</h1>
          <p className="mt-1 text-gray-600">Track your performance, progress, and preparation</p>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            {tabs.map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                  activeTab === tab.key
                    ? 'border-[#0456FF] text-[#0456FF]'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <tab.Icon className="w-4 h-4 mr-2 inline" aria-hidden="true" />{tab.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* ==================== DASHBOARD TAB ==================== */}
        {activeTab === 'dashboard' && analytics && (
          <div className="space-y-8">
            {/* KPI Tiles */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div className="bg-white p-6 rounded-lg border border-gray-200">
                <div className="flex items-center">
                  <div className="p-2 bg-blue-100 rounded-lg"><ClipboardList className="w-6 h-6 text-blue-600" aria-hidden="true" /></div>
                  <div className="ml-4">
                    <p className="text-sm text-gray-600">Tests Taken</p>
                    <p className="text-2xl font-bold text-[#0456FF]">{analytics.totalTests}</p>
                  </div>
                </div>
              </div>
              <div className="bg-white p-6 rounded-lg border border-gray-200">
                <div className="flex items-center">
                  <div className="p-2 bg-green-100 rounded-lg"><BarChart2 className="w-6 h-6 text-green-600" aria-hidden="true" /></div>
                  <div className="ml-4">
                    <p className="text-sm text-gray-600">Average Score</p>
                    <p className="text-2xl font-bold text-[#0456FF]">{analytics.averagePercentage.toFixed(1)}%</p>
                  </div>
                </div>
              </div>
              <div className="bg-white p-6 rounded-lg border border-gray-200">
                <div className="flex items-center">
                  <div className="p-2 bg-yellow-100 rounded-lg"><Trophy className="w-6 h-6 text-yellow-600" aria-hidden="true" /></div>
                  <div className="ml-4">
                    <p className="text-sm text-gray-600">Best Score</p>
                    <p className="text-2xl font-bold text-[#0456FF]">{bestPercentage.toFixed(1)}%</p>
                  </div>
                </div>
              </div>
              <div className="bg-white p-6 rounded-lg border border-gray-200">
                <div className="flex items-center">
                  <div className="p-2 bg-purple-100 rounded-lg"><Timer className="w-6 h-6 text-purple-600" aria-hidden="true" /></div>
                  <div className="ml-4">
                    <p className="text-sm text-gray-600">Study Hours</p>
                    <p className="text-2xl font-bold text-[#0456FF]">{totalStudyHours}h</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="bg-white p-6 rounded-lg border border-gray-200">
                <h3 className="text-lg font-semibold text-[#0456FF] mb-4">Score Trend</h3>
                <MonthlyProgressChart data={analytics.monthlyProgress || []} />
              </div>
              <div className="bg-white p-6 rounded-lg border border-gray-200">
                <h3 className="text-lg font-semibold text-[#0456FF] mb-4">Subject Performance</h3>
                <SubjectPerformanceChart data={subjectPerformanceList} />
              </div>
            </div>

            {/* Recent Tests */}
            <div className="bg-white rounded-lg border border-gray-200">
              <div className="p-6 border-b border-gray-200 flex justify-between items-center">
                <h3 className="text-lg font-semibold text-[#0456FF]">Recent Tests</h3>
                <button
                  onClick={() => setActiveTab('per-exam')}
                  className="text-sm text-[#0456FF] hover:underline"
                >
                  View all →
                </button>
              </div>
              <div className="p-6 space-y-4">
                {recentCompletedResults.length > 0 ? (
                  recentCompletedResults.map(result => (
                    <TestResultCard
                      key={result.id}
                      result={result}
                      onView={() => navigate(`/results/${result.id}`)}
                    />
                  ))
                ) : (
                  <p className="text-gray-500 text-center py-4">No completed tests yet</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ==================== PER-EXAM TAB ==================== */}
        {activeTab === 'per-exam' && (
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Exam Selector Sidebar */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <div className="p-4 border-b border-gray-200">
                  <h3 className="font-semibold text-gray-800">Your Exams</h3>
                  <p className="text-xs text-gray-500 mt-1">Select to view details</p>
                </div>
                <div className="divide-y divide-gray-100 max-h-[600px] overflow-y-auto">
                  {results.map(result => (
                    <button
                      key={result.id}
                      onClick={() => handleSelectResult(result)}
                      className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors ${
                        selectedResult?.id === result.id ? 'bg-blue-50 border-l-4 border-[#0456FF]' : ''
                      }`}
                    >
                      <div className="text-sm font-medium text-gray-900 truncate">
                        {result.testTitle || 'Untitled Test'}
                      </div>
                      <div className="flex items-center justify-between mt-1">
                        <span className="text-xs text-gray-500">
                          {new Date(result.completedAt || result.startedAt).toLocaleDateString()}
                        </span>
                        <span className={`text-xs font-semibold px-1.5 py-0.5 rounded ${getScoreBadgeClass(result.percentage)}`}>
                          {result.percentage.toFixed(0)}%
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Exam Detail Panel */}
            <div className="lg:col-span-3 space-y-6">
              {selectedResult ? (
                <>
                  {/* Score Header Card */}
                  <div className="bg-white rounded-lg border border-gray-200 p-6">
                    <div className="flex items-start justify-between">
                      <div>
                        <h2 className="text-xl font-bold text-gray-900">
                          {selectedResult.testTitle || 'Untitled Test'}
                        </h2>
                        <p className="text-sm text-gray-500 mt-1">
                          {new Date(selectedResult.completedAt || selectedResult.startedAt).toLocaleString()}
                          {' · '}{selectedResult.durationMinutes} min used
                          {selectedResult.timeLimitMinutes ? ` / ${selectedResult.timeLimitMinutes} min limit` : ''}
                        </p>
                      </div>
                      <div className="text-right">
                        <div className={`text-3xl font-bold ${getScoreTextColor(selectedResult.percentage)}`}>
                          {selectedResult.percentage.toFixed(1)}%
                        </div>
                        <div className="text-sm text-gray-500">
                          Grade: <span className="font-semibold">{getGrade(selectedResult.percentage)}</span>
                        </div>
                        {selectedResult.percentile != null && (
                          <div className="text-sm text-gray-500">
                            Top {(100 - selectedResult.percentile).toFixed(0)}% of students
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Score Breakdown */}
                    <div className="mt-6 grid grid-cols-3 gap-4 text-center">
                      <div className="p-3 bg-green-50 rounded-lg border border-green-100">
                        <div className="text-2xl font-bold text-green-700">{selectedResult.correctAnswers}</div>
                        <div className="text-xs text-green-600 mt-1">Correct</div>
                      </div>
                      <div className="p-3 bg-red-50 rounded-lg border border-red-100">
                        <div className="text-2xl font-bold text-red-700">{selectedResult.wrongAnswers}</div>
                        <div className="text-xs text-red-600 mt-1">Wrong</div>
                      </div>
                      <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <div className="text-2xl font-bold text-gray-600">{selectedResult.unansweredQuestions}</div>
                        <div className="text-xs text-gray-500 mt-1">Unanswered</div>
                      </div>
                    </div>

                    {/* Segmented progress bar */}
                    {selectedResult.totalQuestions > 0 && (
                      <div className="mt-4">
                        <div className="w-full h-3 rounded-full bg-gray-200 flex overflow-hidden">
                          <div
                            className="bg-green-500 h-full"
                            style={{ width: `${(selectedResult.correctAnswers / selectedResult.totalQuestions) * 100}%` }}
                          />
                          <div
                            className="bg-red-500 h-full"
                            style={{ width: `${(selectedResult.wrongAnswers / selectedResult.totalQuestions) * 100}%` }}
                          />
                          <div
                            className="bg-gray-300 h-full"
                            style={{ width: `${(selectedResult.unansweredQuestions / selectedResult.totalQuestions) * 100}%` }}
                          />
                        </div>
                        <div className="flex justify-between text-xs text-gray-500 mt-1">
                          <span>{selectedResult.totalQuestions} total questions</span>
                          <span>Score: {selectedResult.score}/{selectedResult.maxScore}</span>
                        </div>
                      </div>
                    )}

                    <div className="mt-5 flex justify-end">
                      <button
                        onClick={() => navigate(`/results/${selectedResult.id}`)}
                        className="px-4 py-2 bg-[#0456FF] text-white text-sm font-medium rounded-lg hover:bg-[#0456FF]/90 transition-colors"
                      >
                        Review Questions →
                      </button>
                    </div>
                  </div>

                  {/* Subject Breakdown */}
                  {selectedResult.subjectResults && selectedResult.subjectResults.length > 0 && (
                    <div className="bg-white rounded-lg border border-gray-200 p-6">
                      <h3 className="text-base font-semibold text-[#0456FF] mb-4">Subject Breakdown</h3>
                      <div className="space-y-3">
                        {selectedResult.subjectResults.map(sr => (
                          <div key={sr.subject}>
                            <div className="flex justify-between text-sm mb-1">
                              <span className="font-medium text-gray-700">{sr.subject}</span>
                              <span className={`font-semibold ${getScoreTextColor(sr.percentage)}`}>
                                {sr.percentage.toFixed(1)}% ({sr.correctAnswers}/{sr.totalQuestions})
                              </span>
                            </div>
                            <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                              <div
                                className={`h-full rounded-full ${getBarColor(sr.percentage)}`}
                                style={{ width: `${Math.min(sr.percentage, 100)}%` }}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Difficulty Breakdown */}
                  {selectedResult.difficultyResult && (
                    <div className="bg-white rounded-lg border border-gray-200 p-6">
                      <h3 className="text-base font-semibold text-[#0456FF] mb-4">Difficulty Breakdown</h3>
                      <div className="grid grid-cols-3 gap-4">
                        <div className="p-4 rounded-lg bg-green-50 border border-green-100">
                          <div className="text-sm font-semibold text-gray-700 mb-2">Easy</div>
                          <div className="text-xl font-bold text-green-700">
                            {selectedResult.difficultyResult.easy?.percentage?.toFixed(0) ?? '—'}%
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            {selectedResult.difficultyResult.easy?.correctAnswers ?? 0}/
                            {selectedResult.difficultyResult.easy?.totalQuestions ?? 0} correct
                          </div>
                        </div>
                        <div className="p-4 rounded-lg bg-yellow-50 border border-yellow-100">
                          <div className="text-sm font-semibold text-gray-700 mb-2">Medium</div>
                          <div className="text-xl font-bold text-yellow-700">
                            {selectedResult.difficultyResult.medium?.percentage?.toFixed(0) ?? '—'}%
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            {selectedResult.difficultyResult.medium?.correctAnswers ?? 0}/
                            {selectedResult.difficultyResult.medium?.totalQuestions ?? 0} correct
                          </div>
                        </div>
                        <div className="p-4 rounded-lg bg-red-50 border border-red-100">
                          <div className="text-sm font-semibold text-gray-700 mb-2">Hard</div>
                          <div className="text-xl font-bold text-red-700">
                            {selectedResult.difficultyResult.hard?.percentage?.toFixed(0) ?? '—'}%
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            {selectedResult.difficultyResult.hard?.correctAnswers ?? 0}/
                            {selectedResult.difficultyResult.hard?.totalQuestions ?? 0} correct
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Behavior Insights (lazy-loaded, silently skipped if unavailable) */}
                  {behaviorLoading && (
                    <div className="bg-white rounded-lg border border-gray-200 p-6 text-center text-sm text-gray-500">
                      Loading behavior data…
                    </div>
                  )}
                  {!behaviorLoading && behaviorData && behaviorData.metrics && (
                    <div className="bg-white rounded-lg border border-gray-200 p-6">
                      <h3 className="text-base font-semibold text-[#0456FF] mb-4">Test-Taking Behavior</h3>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                        <div className="text-center p-3 bg-gray-50 rounded-lg">
                          <div className="text-lg font-bold text-[#0456FF]">
                            {analyticsService.formatTime(behaviorData.metrics.averageTimePerQuestionMs)}
                          </div>
                          <div className="text-xs text-gray-500 mt-1">Avg Time / Q</div>
                        </div>
                        <div className="text-center p-3 bg-gray-50 rounded-lg">
                          <div className="text-lg font-bold text-[#0456FF]">{behaviorData.metrics.totalAnswerChanges}</div>
                          <div className="text-xs text-gray-500 mt-1">Answer Changes</div>
                        </div>
                        <div className="text-center p-3 bg-gray-50 rounded-lg">
                          <div className="text-lg font-bold text-[#0456FF]">{behaviorData.metrics.questionsMarkedForReview}</div>
                          <div className="text-xs text-gray-500 mt-1">Marked for Review</div>
                        </div>
                        <div className="text-center p-3 bg-gray-50 rounded-lg">
                          <div className="text-lg font-bold text-[#0456FF]">{behaviorData.metrics.focusLossCount}</div>
                          <div className="text-xs text-gray-500 mt-1">Focus Losses</div>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                        {[
                          { label: 'Sequential Approach', value: behaviorData.metrics.usedSequentialApproach },
                          { label: 'Review Strategy', value: behaviorData.metrics.usedReviewStrategy },
                          { label: 'Subject Grouping', value: behaviorData.metrics.usedSubjectGrouping },
                        ].map(({ label, value }) => (
                          <div
                            key={label}
                            className={`p-2 rounded border flex items-center justify-between ${
                              value ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-gray-50'
                            }`}
                          >
                            <span className="text-gray-700">{label}</span>
                            <span className={value ? 'text-green-600 font-semibold' : 'text-gray-400'}>
                              {value ? <CheckCircle2 className="w-4 h-4" aria-hidden="true" /> : <XCircle className="w-4 h-4" aria-hidden="true" />}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="bg-white rounded-lg border border-gray-200 p-12 text-center text-gray-500">
                  Select an exam from the list to view details
                </div>
              )}
            </div>
          </div>
        )}

        {/* ==================== WEAK AREAS TAB ==================== */}
        {activeTab === 'weak-areas' && (
          <div className="space-y-8">
            {!analytics ? (
              <div className="bg-white p-8 rounded-lg border border-gray-200 text-center text-gray-500">
                No data available yet
              </div>
            ) : (
              <>
                {/* Insight Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                    <div className="text-xs text-green-700 font-semibold uppercase tracking-wide mb-1">Strongest Subject</div>
                    <div className="text-lg font-bold text-green-800">
                      {subjectPerformanceList[0]?.subject ?? 'N/A'}
                    </div>
                    {subjectPerformanceList[0] && (
                      <div className="text-sm text-green-700">{subjectPerformanceList[0].averagePercentage.toFixed(1)}% accuracy</div>
                    )}
                  </div>
                  <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                    <div className="text-xs text-red-700 font-semibold uppercase tracking-wide mb-1">Needs Most Attention</div>
                    <div className="text-lg font-bold text-red-800">
                      {weakSubjects[0]?.subject ?? 'N/A'}
                    </div>
                    {weakSubjects[0] && (
                      <div className="text-sm text-red-700">{weakSubjects[0].averagePercentage.toFixed(1)}% accuracy</div>
                    )}
                  </div>
                  <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                    <div className="text-xs text-amber-700 font-semibold uppercase tracking-wide mb-1">Suggestion</div>
                    <div className="text-sm text-amber-800">
                      {weakSubjects[0]
                        ? `Prioritize timed practice in ${weakSubjects[0].subject}. Review mistakes from your last 5 tests.`
                        : 'Keep taking tests to get personalized suggestions.'}
                    </div>
                  </div>
                </div>

                {/* Subject Table — sorted weakest first */}
                <div className="bg-white rounded-lg border border-gray-200">
                  <div className="p-6 border-b border-gray-200">
                    <h3 className="text-lg font-semibold text-[#0456FF]">Subject Performance Deep Dive</h3>
                    <p className="text-sm text-gray-500 mt-1">Sorted weakest first — focus where it matters most</p>
                  </div>
                  <div className="overflow-x-auto">
                    {weakSubjects.length > 0 ? (
                      <table className="min-w-full text-sm">
                        <thead>
                          <tr className="border-b bg-gray-50 text-left text-gray-600">
                            <th className="py-3 px-6">Subject</th>
                            <th className="py-3 px-4">Tests</th>
                            <th className="py-3 px-4">Questions</th>
                            <th className="py-3 px-4">Correct</th>
                            <th className="py-3 px-4">Wrong</th>
                            <th className="py-3 px-4">Accuracy</th>
                            <th className="py-3 px-4">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {weakSubjects.map(subject => (
                            <tr key={subject.subject} className="border-b last:border-b-0 hover:bg-gray-50">
                              <td className="py-3 px-6 font-medium text-gray-900">{subject.subject}</td>
                              <td className="py-3 px-4 text-gray-700">{subject.totalTests}</td>
                              <td className="py-3 px-4 text-gray-700">{subject.totalQuestions}</td>
                              <td className="py-3 px-4 text-green-700 font-medium">{subject.totalCorrect}</td>
                              <td className="py-3 px-4 text-red-700 font-medium">{subject.totalWrong}</td>
                              <td className="py-3 px-4">
                                <div className="flex items-center gap-2">
                                  <div className="w-16 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                                    <div
                                      className={`h-full rounded-full ${getBarColor(subject.averagePercentage)}`}
                                      style={{ width: `${subject.averagePercentage}%` }}
                                    />
                                  </div>
                                  <span className={`font-semibold ${getScoreTextColor(subject.averagePercentage)}`}>
                                    {subject.averagePercentage.toFixed(1)}%
                                  </span>
                                </div>
                              </td>
                              <td className="py-3 px-4">
                                <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getScoreBadgeClass(subject.averagePercentage)}`}>
                                  {getStatusLabel(subject.averagePercentage)}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <p className="text-gray-500 text-center py-8">No subject data available</p>
                    )}
                  </div>
                </div>

                {/* Difficulty Chart */}
                {difficultyList.length > 0 && (
                  <div className="bg-white p-6 rounded-lg border border-gray-200">
                    <h3 className="text-lg font-semibold text-[#0456FF] mb-4">Difficulty Analysis</h3>
                    <DifficultyAnalysisChart data={difficultyList} />
                  </div>
                )}
              </>
            )}
          </div>
        )}

      </div>
    </div>
  );
};


